import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-cambiar-password',
  templateUrl: './cambiar-password.component.html',
  styleUrls: ['./cambiar-password.component.css']
})
export class CambiarPasswordComponent implements OnInit {
  changePasswordForm: FormGroup;
  loading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator
      ]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit(): void {
    // Check if user is authenticated
    if (!this.authService.isAuthenticated) {
      this.router.navigate(['/login']);
    }
  }

  // Custom validator for password strength
  passwordStrengthValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const passwordValid = hasUpperCase && hasLowerCase && (hasNumeric || hasSpecialChar);

    return !passwordValid ? { passwordStrength: true } : null;
  }

  // Custom validator for password match
  passwordMatchValidator(group: AbstractControl): { [key: string]: any } | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  get f() {
    return this.changePasswordForm.controls;
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    if (field === 'current') {
      this.showCurrentPassword = !this.showCurrentPassword;
    } else if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  onSubmit(): void {
    this.errorMessage = null;
    this.successMessage = null;

    if (this.changePasswordForm.invalid) {
      Object.keys(this.changePasswordForm.controls).forEach(key => {
        this.changePasswordForm.controls[key].markAsTouched();
      });
      return;
    }

    const { currentPassword, newPassword } = this.changePasswordForm.value;

    this.loading = true;

    this.authService.changePassword(currentPassword, newPassword).subscribe(
      response => {
        this.loading = false;
        this.successMessage = 'Contraseña cambiada exitosamente. Redirigiendo...';
        this.changePasswordForm.reset();
        
        // Redirect to profile after 2 seconds
        setTimeout(() => {
          this.router.navigate(['/mi-perfil']);
        }, 2000);
      },
      error => {
        this.loading = false;
        console.error('Error changing password:', error);
        
        if (error.status === 401) {
          this.errorMessage = 'La contraseña actual es incorrecta.';
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = 'Error al cambiar la contraseña. Por favor, intente nuevamente.';
        }
      }
    );
  }

  cancel(): void {
    this.router.navigate(['/mi-perfil']);
  }

  getPasswordStrengthErrors(): string[] {
    const errors: string[] = [];
    const control = this.f.newPassword;

    if (control.errors?.['passwordStrength'] && control.touched) {
      const value = control.value;
      if (!/[A-Z]/.test(value)) {
        errors.push('una letra mayúscula');
      }
      if (!/[a-z]/.test(value)) {
        errors.push('una letra minúscula');
      }
      if (!/[0-9]/.test(value) && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
        errors.push('un número o carácter especial');
      }
    }
    return errors;
  }
}